/**
 * System Edit Component
 */

import {
  LIMIT_FIELDS,
  LimitField,
  LimitValues,
  finiteDecimal,
} from '../../../../../../../common/thermostat-limits';
import {
  displayLimitBounds,
  resolveDeviceLimitDomains,
  canEditDeviceLimits,
  validateDisplayLimits,
} from '../../lib/limit-domains';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { Maybe } from 'graphql/jsutils/Maybe';

/* MUI */
import {
  Button,
  capitalize,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
  Switch,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Divider from '@mui/material/Divider';
import NativeSelect from '@mui/material/NativeSelect';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';

/* Icons */
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import '../status-icons/status-icons';

/* Custom Components */
import BorderedSection from '../../../../../shared/borderedSection';
import { Notifier } from '../../../../../../system/services/notificationManager';
import { systemChoices, getOperatingModeChoices } from '../helpers/choices';
import { TemperatureUnit } from '../../../../../../system/models/temperatureUnits';
import { useIsSmallScreen } from '../../../../../../system/SystemMediaQueriesProvider';

/* Types */
import { CurrentAttributes, SetpointProfile, MyThermostat } from '../../types';
import {
  getMitsubishiOperatingModes,
  getMitsubishiFanSpeeds,
  mitsubishiFanSpeedLabel,
} from '../../lib/mitsubishi-power-edit';

interface SystemEditProps {
  canEditPowerMode?: boolean;
  canEditOperatingMode?: boolean;
  canEditFanMode?: boolean;
  canEditFanSpeed?: boolean;
  currentValues: CurrentAttributes;
  handleUseCustomSetpointLimitsChange: (newValue: boolean) => void;
  propertySetpointProfile?: Maybe<SetpointProfile>;
  unitSetpointProfile?: Maybe<SetpointProfile>;
  thermostat?: MyThermostat;
  operatingMode?: Maybe<string>;
  systemType?: Maybe<string>;
  fanMode?: Maybe<string>;
  fanSpeed?: Maybe<string>;
  keypadLockoutLevel?: Maybe<number>;
  progMode?: Maybe<string>;
  maxCoolSetpointLimit?: Maybe<number>;
  maxHeatSetpointLimit?: Maybe<number>;
  minCoolSetpointLimit?: Maybe<number>;
  minHeatSetpointLimit?: Maybe<number>;
  schedVisibilityMode?: Maybe<string>;
  holdState?: Maybe<string>;
  holdDuration?: Maybe<number>;
  holdEnd?: Maybe<number>;
  handleChange: (
    changes: Record<string, number | string | boolean>,
    ensureConformity?: boolean,
  ) => void;
  handleSetpointLimitChange: (
    key: string,
    value: number,
  ) => Record<string, number | string | boolean>;
  handleSetpointLimitsChange?: (values: LimitValues) => boolean;
  onLimitDraftValidityChange?: (invalid: boolean) => void;
  targetUnits: TemperatureUnit;
}
export function SystemEdit(props: SystemEditProps) {
  const {
    currentValues,
    thermostat,
    handleChange,
    handleSetpointLimitChange,
    handleUseCustomSetpointLimitsChange,
    // TODO: CleanUp: if this prop is not useful (it is not used anywhere), remove it.
    targetUnits,
    onLimitDraftValidityChange,
  } = props;
  const isMitsubishi = thermostat?.isMitsubishiThermostat === true;
  const separatePower = !!thermostat?.separatePowerControlV1;

  const [
    openCustomSetpointLimitWarningDialog,
    setOpenCustomSetpointLimitWarningDialog,
  ] = useState(false);

  const supportedSystemTypes = systemChoices.systemTypes.filter(
    (type) =>
      thermostat?.supportsAlternateHeatSystemType ||
      (thermostat?.modbusRTU && type.id === 'SmartAHU') ||
      type.id !== 'AlternateHeat',
  );

  const theme = useTheme();
  const isSmall = useIsSmallScreen();

  enum MenuItems {
    OPERATING_MODE = 'operating_mode',
    FAN_MODE = 'fan_mode',
    SYSTEM_TYPE = 'system_type',
    SETPOINTS = 'setpoints',
    SCHEDULE_MODE = 'schedule_mode',
  }

  const limitDomains = resolveDeviceLimitDomains(thermostat);
  const [limitDrafts, setLimitDrafts] = useState<
    Partial<Record<LimitField, string>>
  >({});
  const [invalidLimitDraft, setInvalidLimitDraft] = useState(false);
  const inputContext = JSON.stringify([
    targetUnits,
    currentValues.useCustomSetpointLimits,
    ...LIMIT_FIELDS.map((field) =>
      currentValues.useCustomSetpointLimits
        ? currentValues[field]
        : currentValues[
            `base${field[0].toUpperCase()}${field.slice(1)}` as keyof CurrentAttributes
          ],
    ),
  ]);
  // Unrelated target/fan edits must not erase an incomplete input. Reset only
  // after the committed limits, units or selected policy change (or remount).
  useEffect(() => {
    const [, , ...limits] = JSON.parse(inputContext);
    setLimitDrafts(
      Object.fromEntries(
        LIMIT_FIELDS.map((field, index) => [
          field,
          limits[index] == null ? '' : String(limits[index]),
        ]),
      ),
    );
    setInvalidLimitDraft(false);
    onLimitDraftValidityChange?.(false);
  }, [inputContext, onLimitDraftValidityChange]);

  const changeLimitDraft = (field: LimitField, text: string) => {
    const drafts = { ...limitDrafts, [field]: text };
    setLimitDrafts(drafts);
    const limits = Object.fromEntries(
      LIMIT_FIELDS.map((key) => [key, finiteDecimal(drafts[key])]),
    ) as Partial<LimitValues>;
    let invalid =
      validateDisplayLimits(limits, thermostat, targetUnits).length > 0;
    if (!invalid) {
      if (props.handleSetpointLimitsChange)
        invalid = !props.handleSetpointLimitsChange(limits as LimitValues);
      else handleSetpointLimitChange(field, limits[field]!);
    }
    setInvalidLimitDraft(invalid);
    onLimitDraftValidityChange?.(invalid);
  };

  const [expand, setExpand] = useState<Record<MenuItems, boolean>>({
    [MenuItems.OPERATING_MODE]: true,
    [MenuItems.FAN_MODE]: true,
    [MenuItems.SYSTEM_TYPE]: true,
    [MenuItems.SETPOINTS]: true,
    [MenuItems.SCHEDULE_MODE]: true,
  });

  const [values, setValues] = useState<
    Partial<CurrentAttributes> & {
      setpointLimitType?: string | boolean | number | undefined | null;
    }
  >();

  useEffect(() => {
    if (currentValues) {
      const {
        operatingMode,
        systemType,
        fanMode,
        progMode,
        baseMaxCoolSetpointLimit,
        baseMaxHeatSetpointLimit,
        baseMinCoolSetpointLimit,
        baseMinHeatSetpointLimit,
        maxCoolSetpointLimit,
        maxHeatSetpointLimit,
        minCoolSetpointLimit,
        minHeatSetpointLimit,
        keypadLockoutLevel,
        useCustomSetpointLimits,
        setpointLimitBasis,
        // TODO: CleanUp: do we need these any longer? If not, remove them?
        // schedVisibilityMode,
        // showFanButton,
        // supportsShowFanButton,
        // supportsPasscode,
        // supportsSystemType,
        // supportsRemoteSensorTempDelta,
        // supportsLastSetpointChange,
        // supportsFanOperations,
      } = currentValues;

      setValues({
        ...currentValues,
        operatingMode: operatingMode ?? 'Off',
        systemType: systemType ?? 'HeatAndCool',
        fanMode: fanMode ?? 'Off',
        progMode: progMode ?? 'SIMPLE_SETPOINT_MODE',
        keypadLockoutLevel: keypadLockoutLevel ?? '0',
        minCoolSetpointLimit: useCustomSetpointLimits
          ? minCoolSetpointLimit
          : baseMinCoolSetpointLimit,
        maxCoolSetpointLimit: useCustomSetpointLimits
          ? maxCoolSetpointLimit
          : baseMaxCoolSetpointLimit,
        minHeatSetpointLimit: useCustomSetpointLimits
          ? minHeatSetpointLimit
          : baseMinHeatSetpointLimit,
        maxHeatSetpointLimit: useCustomSetpointLimits
          ? maxHeatSetpointLimit
          : baseMaxHeatSetpointLimit,
        useCustomSetpointLimits: useCustomSetpointLimits ?? false,
        setpointLimitType: useCustomSetpointLimits
          ? 'custom'
          : (setpointLimitBasis ?? 'default'),
      });
    }
  }, [currentValues]);

  const fanModeLabel = isMitsubishi
    ? 'Selected Fan Speed'
    : thermostat?.isMultiSpeedThermostat
      ? 'Fan Speed Mode'
      : isSmall
        ? 'Fan Speed Cycling Mode'
        : 'Fan Single-Speed Cycling Mode';

  const fanModeChoices = thermostat?.isMultiSpeedThermostat
    ? systemChoices.fanSpeedModes
    : thermostat?.modbusRTU
      ? systemChoices.fanDutyCycleModes.filter(
          (choice) => choice.id !== 'SmartAHU',
        )
      : systemChoices.fanDutyCycleModes;

  const powerSnapshot = thermostat?.separatePowerControlV1;
  const fanSpeeds = powerSnapshot ? getMitsubishiFanSpeeds(powerSnapshot) : [];
  const canEditFanSpeed =
    props.canEditFanSpeed !== false &&
    powerSnapshot?.canEditFanSpeed === true &&
    fanSpeeds.length > 0 &&
    !powerSnapshot.powerMode?.pending &&
    !powerSnapshot.operatingMode?.pending &&
    !powerSnapshot.fanSpeed?.pending &&
    !powerSnapshot.airDirection?.pending;
  const fanSpeedHelp = powerSnapshot?.fanSpeed?.pending
    ? 'Awaiting fan speed confirmation.'
    : powerSnapshot?.powerMode?.pending ||
        powerSnapshot?.operatingMode?.pending ||
        powerSnapshot?.airDirection?.pending
      ? 'Wait for the pending change to be confirmed.'
      : fanSpeeds.length === 0
        ? 'Awaiting fan-speed capabilities from Conductor.'
        : powerSnapshot?.canEditFanSpeed !== true
          ? 'Fan-speed changes are not enabled for this source.'
          : props.canEditFanSpeed === false
            ? 'Fan-speed editing is unavailable for this session.'
            : 'Auto lets the equipment choose its speed.';
  const operatingModes =
    isMitsubishi && powerSnapshot
      ? getMitsubishiOperatingModes(powerSnapshot)
          .filter(
            (mode) => mode !== 'Auto' || values?.supportsAutoMode !== false,
          )
          .map((id) => ({ id, label: id === 'Fan' ? 'Fan only' : id }))
      : getOperatingModeChoices(
          values?.systemType ?? undefined,
          values?.supportsAutoMode,
        ).filter((choice) => !separatePower || choice.id !== 'Off');
  const canEditOperatingMode =
    props.canEditOperatingMode !== false &&
    (!powerSnapshot ||
      (powerSnapshot.canEditOperatingMode &&
        !powerSnapshot.powerMode?.pending &&
        !powerSnapshot.operatingMode?.pending &&
        !powerSnapshot.fanSpeed?.pending &&
        !powerSnapshot.airDirection?.pending)) &&
    operatingModes.length > 0;

  useEffect(() => {
    if (!values || isMitsubishi) return;
    const operatingMode = values.operatingMode;
    let correctionMade = false;

    switch (values.systemType) {
      case 'SmartAHU':
      case 'HeatAndCool':
      case 'HeatPump':
        // Valid operatingModes are: Cool, Heat, Auto, Off - all the possible values
        // So never have to change operatingMode when systemType changed to HeatAndCool
        break;
      case 'CoolOnly':
        // Valid operatingModes are: Cool, Off
        if (operatingMode !== 'Cool' && operatingMode !== 'Off') {
          // Change operating mode to Off
          values.operatingMode = 'Off';
          correctionMade = true;
        }
        break;
      default:
        // All other Heat types
        // Valid operatingModes are: Heat, Off
        if (operatingMode !== 'Heat' && operatingMode !== 'Off') {
          // Change operating mode to Off
          values.operatingMode = 'Off';
          correctionMade = true;
        }
        break;
    }
    if (correctionMade) {
      Notifier.warn(
        `${operatingMode} Mode invalid for ${values.systemType} HVAC System Type`,
        `Operating Mode has been changed to Off!`,
      );
    }
  }, [values, isMitsubishi]);

  const handleExpand = (menuItem: MenuItems) => {
    setExpand((current) => {
      return { ...current, [menuItem]: !current[menuItem] };
    });
  };

  const handleOperatingModeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    if (!canEditOperatingMode) return;
    const value = event.target.value;
    if (!operatingModes.some((choice) => choice.id === value)) return;
    setValues((current) => ({ ...current, operatingMode: value }));
    handleChange({ operatingMode: value });
  };

  const handleFanModeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    if (props.canEditFanMode === false) return;
    const value = event.target.value;
    setValues((current) => ({ ...current, fanMode: value }));
    handleChange({ fanMode: value });
  };

  const handleSystemTypeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setValues((current) => ({ ...current, systemType: value }));
    handleChange({ systemType: value });
  };

  const handleShowFanSwitchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked;
    setValues((current) => ({
      ...current,
      showFanButton: value,
    }));
    handleChange({ showFanButton: value });
  };

  const handleKeyboardLockoutLevelChange = (
    event: ChangeEvent<HTMLSelectElement>,
  ) => {
    const value = event.target.value ?? '0';
    setValues((current) => ({
      ...current,
      keypadLockoutLevel: value,
    }));
    handleChange({ keypadLockoutLevel: value });
  };

  const handleOpenCustomSetpointLimitWarningDialog = () => {
    setOpenCustomSetpointLimitWarningDialog(true);
  };

  const handleCloseCustomSetpointLimitWarningDialog = () => {
    setOpenCustomSetpointLimitWarningDialog(false);
  };

  // TODO: CleanUp: is this still needed?
  // const _handleUseCustomSetpointLimitsChange = (newValue: boolean) => {
  //   const value = newValue;
  //   console.log('[handle-use-custom-setpoint-limits-change]', value);
  //   setValues((current) => ({
  //     ...current,
  //     useCustomSetpointLimits: value,
  //   }));
  //   handleUseCustomSetpointLimitsChange(newValue);
  // };

  return values ? (
    <>
      <Divider
        style={{ marginBottom: '12px' }}
        variant="middle"
        component="hr"
      />
      <Box sx={{ p: 0 }}>
        <BorderedSection
          title="Thermostat Control"
          labelStyle={{ fontSize: '10px' }}
          style={{ marginLeft: '4px', marginRight: '4px', padding: '0px' }}
          ccStyle={{ margin: '0px', padding: '0px' }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              padding: '0px',
              margin: '0px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              {thermostat?.supportsSystemType && (
                <BorderedSection
                  title="HVAC System Type"
                  labelStyle={{ fontSize: '10px' }}
                  style={{ margin: '4px 4px 4px 4px' }}
                  ccStyle={{
                    width: '100%',
                    paddingTop: '0px',
                    paddingLeft: '8px',
                    paddingRight: '8px',
                    paddingBottom: '8px',
                  }}
                >
                  {values.modbusRTU ? (
                    <div style={{ width: '100%' }}>Smart AHU</div>
                  ) : (
                    <NativeSelect
                      value={values.systemType}
                      onChange={handleSystemTypeChange}
                      style={{ width: '100%' }}
                    >
                      {supportedSystemTypes.map((choice) => (
                        <option
                          key={`${choice.id}=${choice.label}`}
                          value={choice.id}
                          label={choice.label}
                        >
                          {choice.label}
                        </option>
                      ))}
                    </NativeSelect>
                  )}
                </BorderedSection>
              )}
              {!isMitsubishi && (
                <BorderedSection
                  title="Menu Lockout Settings"
                  labelStyle={{ fontSize: '10px' }}
                  style={{ margin: '4px 4px 4px 4px' }}
                  ccStyle={{
                    width: '100%',
                    paddingTop: '0px',
                    paddingLeft: '8px',
                    paddingRight: '8px',
                    paddingBottom: '8px',
                  }}
                >
                  <NativeSelect
                    value={values.keypadLockoutLevel}
                    onChange={handleKeyboardLockoutLevelChange}
                    style={{ width: '100%' }}
                  >
                    {systemChoices.lockoutLevels
                      .filter((choice) => {
                        return values.keypadLockoutLevels?.includes(choice.id);
                      })
                      .map((choice) => (
                        <option
                          key={`${choice.id}=${choice.label}`}
                          value={choice.id}
                          label={choice.label}
                        >
                          {choice.label}
                        </option>
                      ))}
                  </NativeSelect>
                </BorderedSection>
              )}
            </Box>
            <Box
              sx={{
                display: isMitsubishi ? 'grid' : 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                marginTop: '4px',
                flexWrap: 'nowrap',
                gridTemplateColumns: isMitsubishi
                  ? separatePower
                    ? '88px minmax(0, 1fr)'
                    : 'minmax(0, 1fr)'
                  : undefined,
                '& > *': isMitsubishi ? { minWidth: 0 } : undefined,
              }}
            >
              {separatePower && (
                <BorderedSection
                  title="Power"
                  labelStyle={{ fontSize: '10px' }}
                  style={{ margin: '4px' }}
                  ccStyle={{ padding: '0 8px 8px' }}
                >
                  <NativeSelect
                    data-testid="thermostat-powerMode-select"
                    inputProps={{ 'aria-label': 'Power' }}
                    value={values.powerMode ?? ''}
                    disabled={props.canEditPowerMode === false}
                    onChange={(event) => {
                      if (props.canEditPowerMode === false) return;
                      const value = event.target.value;
                      setValues((current) => ({
                        ...current,
                        powerMode: value,
                      }));
                      handleChange({ powerMode: value });
                    }}
                  >
                    {!['On', 'Off'].includes(values.powerMode ?? '') && (
                      <option value="" disabled>
                        Unknown
                      </option>
                    )}
                    <option value="On">On</option>
                    <option value="Off">Off</option>
                  </NativeSelect>
                </BorderedSection>
              )}
              <BorderedSection
                title="Operating Mode"
                labelStyle={{ fontSize: '10px' }}
                style={{ margin: '4px 4px 4px 4px' }}
                ccStyle={{
                  width: '100%',
                  paddingTop: '0px',
                  paddingLeft: '8px',
                  paddingRight: '8px',
                  paddingBottom: '8px',
                }}
              >
                <NativeSelect
                  data-testid="thermostat-operatingMode-select"
                  disabled={!canEditOperatingMode}
                  value={values.operatingMode}
                  onChange={handleOperatingModeChange}
                  style={{ width: '100%' }}
                >
                  {separatePower &&
                    !operatingModes.some(
                      (choice) => choice.id === values.operatingMode,
                    ) && (
                      <option value={values.operatingMode ?? ''} disabled>
                        {values.operatingMode === 'Fan'
                          ? 'Fan only'
                          : (values.operatingMode ?? 'Unknown')}
                      </option>
                    )}
                  {(operatingModes ?? []).map((choice) => (
                    <option
                      key={`${choice.id}=${choice.label}`}
                      value={choice.id}
                      label={choice.label}
                    >
                      {choice.label}
                    </option>
                  ))}
                </NativeSelect>
              </BorderedSection>

              <BorderedSection
                title={fanModeLabel}
                labelStyle={{ fontSize: '10px' }}
                style={{ margin: '4px' }}
                containerStyle={
                  isMitsubishi ? { gridColumn: '1 / -1' } : undefined
                }
                ccStyle={{
                  width: '100%',
                  paddingTop: '0px',
                  paddingLeft: '8px',
                  paddingRight: '8px',
                  paddingBottom: '8px',
                }}
              >
                {isMitsubishi ? (
                  <>
                    <NativeSelect
                      data-testid="thermostat-fanSpeed-select"
                      inputProps={{ 'aria-label': 'Selected fan speed' }}
                      disabled={!canEditFanSpeed}
                      value={values.fanSpeed ?? ''}
                      onChange={(event) => {
                        if (
                          !canEditFanSpeed ||
                          !fanSpeeds.includes(event.target.value)
                        )
                          return;
                        setValues({
                          ...values,
                          fanSpeed: event.target.value,
                        });
                        handleChange({ fanSpeed: event.target.value }, false);
                      }}
                      style={{ width: '100%' }}
                    >
                      {!fanSpeeds.includes(values.fanSpeed ?? '') && (
                        <option value={values.fanSpeed ?? ''} disabled>
                          {values.fanSpeed
                            ? mitsubishiFanSpeedLabel(values.fanSpeed)
                            : 'Unknown'}
                        </option>
                      )}
                      {fanSpeeds.map((speed) => (
                        <option key={speed} value={speed}>
                          {mitsubishiFanSpeedLabel(speed)}
                        </option>
                      ))}
                    </NativeSelect>
                    <Typography variant="caption" component="div">
                      {fanSpeedHelp}
                    </Typography>
                  </>
                ) : values.modbusRTU ? (
                  <div style={{ width: '100%' }}>Smart AHU</div>
                ) : (
                  <NativeSelect
                    data-testid="thermostat-fanMode-select"
                    disabled={props.canEditFanMode === false}
                    value={values.fanMode}
                    onChange={handleFanModeChange}
                    style={{ width: '100%' }}
                  >
                    {fanModeChoices.map((choice) => (
                      <option
                        key={`${choice.id}=${choice.label}`}
                        value={choice.id}
                        label={choice.label}
                      >
                        {choice.label}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </BorderedSection>
            </Box>
            {values.supportsShowFanButton && !values.modbusRTU ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  marginTop: '4px',
                }}
              >
                <BorderedSection
                  title="Fan Button"
                  style={{ margin: '4px 4px 4px 4px' }}
                  ccStyle={{
                    width: '100%',
                    paddingTop: '0px',
                    paddingLeft: '8px',
                    paddingRight: '8px',
                    paddingBottom: '8px',
                  }}
                >
                  <FormGroup style={{ paddingLeft: '6px' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          onChange={handleShowFanSwitchChange}
                          name="isSuper"
                          id="isSuper"
                          checked={values.showFanButton as boolean}
                        />
                      }
                      label="Show Fan Button"
                    />
                  </FormGroup>
                </BorderedSection>
              </Box>
            ) : null}
            {canEditDeviceLimits(thermostat) && (
              <BorderedSection
                title={`Setpoint Limits (°${targetUnits})`}
                style={{ margin: '8px 4px 4px 4px', padding: '0px' }}
              >
                <List>
                  {/* Setpoint Limits */}
                  <ListItemButton
                    onClick={() => handleExpand(MenuItems.SETPOINTS)}
                  >
                    <Stack
                      width={'100%'}
                      direction={'row'}
                      alignItems={'center'}
                      justifyContent={'space-between'}
                    >
                      <ListItemText
                        primary={`Using ${capitalize(
                          (values.setpointLimitType as string) ?? 'default',
                        )} Limits`}
                      />
                      {['Heat', 'Auto', 'Off'].includes(
                        values.operatingMode as string,
                      ) ? (
                        <ListItemText
                          secondaryTypographyProps={{
                            variant: 'caption',
                            align: 'right',
                          }}
                          secondary={
                            <Chip
                              sx={{ backgroundColor: theme.embue.heating }}
                              label={`${values.minHeatSetpointLimit}-${values.maxHeatSetpointLimit}`}
                            />
                          }
                        />
                      ) : null}
                      {['Cool', 'Auto'].includes(
                        values.operatingMode as string,
                      ) ? (
                        <ListItemText
                          secondaryTypographyProps={{
                            pr: 1,
                            variant: 'caption',
                            align: 'right',
                          }}
                          secondary={
                            <Chip
                              sx={{ backgroundColor: theme.embue.cooling }}
                              label={`${values.minCoolSetpointLimit}-${values.maxCoolSetpointLimit}`}
                            />
                          }
                        />
                      ) : null}
                    </Stack>
                    {expand.setpoints ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                  <Collapse
                    in={expand.setpoints}
                    timeout={'auto'}
                    unmountOnExit
                  >
                    <Box
                      sx={{
                        justifyContent: 'center',
                        px: 4,
                        py: 2,
                      }}
                    >
                      <Stack
                        spacing={2}
                        direction={'row'}
                        alignItems={'center'}
                        justifyContent={'space-between'}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!values.useCustomSetpointLimits}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                if (thermostat?.thermostatScheduleTemplateId) {
                                  handleOpenCustomSetpointLimitWarningDialog();
                                } else {
                                  handleUseCustomSetpointLimitsChange(
                                    !values.useCustomSetpointLimits,
                                  );
                                }
                              }}
                            />
                          }
                          label="Use Custom Setpoint Limits"
                        />
                      </Stack>
                      <Grid container spacing={2}>
                        {LIMIT_FIELDS.map((field) => {
                          const bounds = displayLimitBounds(
                            thermostat,
                            field,
                            targetUnits,
                          );
                          const domain = limitDomains.ok
                            ? limitDomains.value[field]
                            : undefined;
                          return (
                            <Grid item xs={12} sm={6} key={field}>
                              <TextField
                                fullWidth
                                type="number"
                                label={`${field.startsWith('min') ? 'Minimum' : 'Maximum'} ${field.includes('Heat') ? 'heat' : 'cool'} limit (°${targetUnits})`}
                                disabled={!values.useCustomSetpointLimits}
                                value={limitDrafts[field] ?? ''}
                                error={invalidLimitDraft}
                                inputProps={{
                                  min: bounds?.min,
                                  max: bounds?.max,
                                  step: bounds?.step ?? 'any',
                                }}
                                helperText={
                                  invalidLimitDraft
                                    ? 'Enter all four valid limits inside the published ranges.'
                                    : bounds
                                      ? `${bounds.min}–${bounds.max} °${targetUnits}`
                                      : domain
                                        ? 'Enter a value inside the published open interval.'
                                        : 'Equipment limit domain unavailable.'
                                }
                                onChange={(event) => {
                                  changeLimitDraft(field, event.target.value);
                                }}
                              />
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  </Collapse>
                </List>
              </BorderedSection>
            )}
          </Box>
        </BorderedSection>
      </Box>
      <CustomThermostatSetpointLimitChangeWarningDialog
        handleClose={handleCloseCustomSetpointLimitWarningDialog}
        open={openCustomSetpointLimitWarningDialog}
        handleUseCustomSetpointLimitsChange={
          handleUseCustomSetpointLimitsChange
        }
        values={values}
      />
    </>
  ) : null;
}

function CustomThermostatSetpointLimitChangeWarningDialog(props: {
  handleClose: () => void;
  values: Partial<CurrentAttributes>;
  handleUseCustomSetpointLimitsChange: (b: boolean) => void;
  open: boolean;
}) {
  const { handleClose, open, values, handleUseCustomSetpointLimitsChange } =
    props;

  return (
    <Dialog
      open={open}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        alignItems: 'stretch',
      }}
    >
      <Typography variant="h4" sx={{ padding: '15px' }}>
        Warning: Custom Setpoint Limit incompatible with current thermostat
        schedule template
      </Typography>
      <DialogContent sx={{ padding: '10px 10px' }}>
        <Typography>
          Using a custom setpoint limit will remove the current schedule
          template applied to the thermostat. Do you wish to continue?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Grid container rowSpacing={1} direction={'row'}>
          <Grid item xs={12}>
            <Button
              variant="contained"
              fullWidth
              color="warning"
              sx={{ color: '#fff' }}
              onClick={() => {
                handleUseCustomSetpointLimitsChange(
                  !values.useCustomSetpointLimits,
                );
                handleClose();
              }}
            >
              Continue
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Button
              fullWidth
              color="error"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleClose();
              }}
            >
              Cancel
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
}
